
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Maximize2, RefreshCw, Camera, Minimize2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { differenceInDays } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { useRoleFlags } from "@/hooks/use-role-flags";
import { useAuth } from "@/contexts/AuthContext";
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';

interface ImageKit {
  url: string;
  fileId: string;
  name: string;
}

interface DeviceImage {
  device_id: string;
  url?: string;
  device_name?: string;
  description?: string;
}

const isImageKit = (value: unknown): value is ImageKit => {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.url === 'string' &&
    typeof obj.fileId === 'string' &&
    typeof obj.name === 'string'
  );
};

const GalleryContent = ({ standalone = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [lastRealtimeUpdate, setLastRealtimeUpdate] = useState<Date | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { session } = useAuth();
  const { role, isSystemAdmin } = useSimpleRole();
  const { isDemoOnly, hasSuperuserSurface } = useRoleFlags();
  const showAllDevicesFleet = isSystemAdmin || hasSuperuserSurface || isDemoOnly;

  // Set up realtime subscription for device details updates
  useEffect(() => {
    console.log('🖼️ [Gallery] Setting up realtime subscription for device details');
    
    const channel = supabase
      .channel(`gallery-device-details-${Date.now()}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'device_details'
      }, (payload) => {
        console.log('🖼️ [Gallery] Realtime device details update received:', payload);
        setLastRealtimeUpdate(new Date());
        
        // Invalidate the device images query to refetch data
        queryClient.invalidateQueries({ queryKey: ["device-images", isSystemAdmin, role] });
      })
      .subscribe((status) => {
        console.log('🖼️ [Gallery] Device details subscription status:', status);
      });

    return () => {
      console.log('🖼️ [Gallery] Cleaning up device details subscription');
      channel.unsubscribe().then(() => {
        supabase.removeChannel(channel);
      });
    };
  }, [queryClient, isSystemAdmin, role]);

  const { data: deviceImages = [], isLoading } = useQuery({
    queryKey: ["device-images", isSystemAdmin, role, isDemoOnly, hasSuperuserSurface],
    queryFn: async () => {
      console.log('🖼️ [Gallery] Starting device images query');
      console.log('🖼️ [Gallery] User role:', role, 'isSystemAdmin:', isSystemAdmin);
      
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData?.session) {
        console.log('🖼️ [Gallery] No session found, redirecting to login');
        navigate('/');
        throw new Error("Not authenticated");
      }

      if (showAllDevicesFleet) {
        // Superuser / internal_staff / advisor: Get all devices then demo-filter
        const { data: deviceDetails, error } = await supabase
          .from('device_details')
          .select(`
            device_id,
            device_name,
            device_description,
            imagekit,
            device_last_updated,
            device_status
          `);
        
        if (error) {
          console.error("🖼️ [Gallery] Error fetching all device details:", error);
          throw error;
        }

        const visibleDevices = isDemoOnly
          ? (deviceDetails || []).filter(d => (d as any).device_status?.toLowerCase() === 'demo')
          : (deviceDetails || []);

        const images: DeviceImage[] = visibleDevices
          .filter(device => {
            const lastUpdated = device.device_last_updated;
            const isRecentlyUpdated = lastUpdated
              ? differenceInDays(new Date(), new Date(lastUpdated)) < 2
              : false;
            return device.imagekit && isRecentlyUpdated;
          })
          .map(device => {
            const imagekitData = device.imagekit;
            const imagekit = isImageKit(imagekitData) ? imagekitData : null;
            return {
              device_id: device.device_id,
              url: imagekit?.url,
              device_name: device.device_name,
              description: device.device_description
            };
          });

        console.log("🖼️ [Gallery] All device images filtered:", images.length);
        return images;
        
      } else {
        // Regular user: Get only assigned device images
        console.log('🖼️ [Gallery] Regular user detected - fetching assigned device images only');
        console.log('🖼️ [Gallery] Querying device assignments for user:', sessionData.session.user.id);

        const { data: assignments, error: assignmentsError } = await supabase
          .from('device_assignments')
          .select(`
            device_id,
            device_details:device_details!device_assignments_device_id_fkey(
              device_name,
              device_description,
              imagekit,
              device_last_updated
            )
          `)
          .eq('user_id', sessionData.session.user.id);

        if (assignmentsError) {
          console.error("🖼️ [Gallery] Error fetching assignments:", assignmentsError);
          throw assignmentsError;
        }

        if (!assignments || assignments.length === 0) {
          console.log("🖼️ [Gallery] No device assignments found for user");
          return [];
        }

        console.log("🖼️ [Gallery] Raw assignment data:", assignments);

        const images: DeviceImage[] = assignments
          .filter(assignment => {
            const lastUpdated = assignment.device_details?.device_last_updated;
            const isRecentlyUpdated = lastUpdated
              ? differenceInDays(new Date(), new Date(lastUpdated)) < 2
              : false;
            return assignment.device_details?.imagekit && isRecentlyUpdated;
          })
          .map(assignment => {
            const imagekitData = assignment.device_details.imagekit;
            const imagekit = isImageKit(imagekitData) ? imagekitData : null;
            return {
              device_id: assignment.device_id,
              url: imagekit?.url,
              device_name: assignment.device_details.device_name,
              description: assignment.device_details.device_description
            };
          });

        console.log("🖼️ [Gallery] Assigned device images filtered:", images.length);
        return images;
      }
    },
    enabled: !!session && role !== undefined && isSystemAdmin !== undefined, // Only run when session and role are stable
    staleTime: Infinity, // Never consider data stale - only refresh manually
    refetchInterval: false, // No automatic polling
    refetchOnWindowFocus: false, // No refresh on window focus
    refetchOnMount: false, // Don't refetch on component remount
  });

  useEffect(() => {
    if (!deviceImages.length) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((current) => 
        current === deviceImages.length - 1 ? 0 : current + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [deviceImages]);

  useEffect(() => {
    if (standalone) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session) {
          window.location.href = '/';
        }
      });
    }
  }, [standalone]);

  const handleRefresh = () => {
    console.log('Manual refresh triggered for gallery');
    toast.promise(
      queryClient.invalidateQueries({ queryKey: ["device-images", isSystemAdmin, role] }),
      {
        loading: "Refreshing gallery...",
        success: "Gallery refreshed",
        error: "Failed to refresh gallery"
      }
    );
  };

  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        const element = document.documentElement;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if ((element as any).webkitRequestFullscreen) {
          await (element as any).webkitRequestFullscreen();
        } else if ((element as any).msRequestFullscreen) {
          await (element as any).msRequestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
      toast.error('Failed to toggle fullscreen mode');
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  return (
    <div className="flex-1">
      <FixedPageHeader
        {...PAGE_HEADERS.gallery}
        actions={
          <div className="flex gap-2">
            <Button
              onClick={handleRefresh}
              variant="outline"
              size="sm"
              className="gap-2 text-xs"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </Button>
            {!standalone && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        }
      />
      <div className="px-4 md:px-8 py-6 md:py-8 space-y-8" style={{ paddingTop: "8.5rem" }}>
        {/* Status indicator for realtime updates */}
        {lastRealtimeUpdate && (
          <div className="text-xs text-muted-foreground">
            Last device update: {lastRealtimeUpdate.toLocaleTimeString()}
          </div>
        )}
        
        {/* Debug info */}
        {isLoading && (
          <div className="text-sm text-muted-foreground">Loading images...</div>
        )}

        {!isLoading && !deviceImages.length && (
          <Card className="p-6">
            <div className="flex flex-col items-center gap-2 text-center">
              <Camera className="h-8 w-8 text-muted-foreground" />
              <p className="text-muted-foreground">No images available</p>
            </div>
          </Card>
        )}

        {!isLoading && deviceImages.length > 0 && (
          <div className="space-y-6">
            <Card className="overflow-hidden">
              <AspectRatio ratio={16/9}>
                {deviceImages[currentImageIndex]?.url ? (
                  <img
                    src={deviceImages[currentImageIndex].url}
                    alt={`Image from ${deviceImages[currentImageIndex].device_name || deviceImages[currentImageIndex].device_id}`}
                    className="w-full h-full object-contain bg-background cursor-pointer"
                    onClick={() => setShowImageDialog(true)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-background">
                    <p className="text-muted-foreground">No image available</p>
                  </div>
                )}
              </AspectRatio>
              <div className="p-4">
                <p className="font-medium">
                  {deviceImages[currentImageIndex]?.device_name || deviceImages[currentImageIndex]?.device_id}
                </p>
                {deviceImages[currentImageIndex]?.description && (
                  <p className="text-sm text-muted-foreground">
                    {deviceImages[currentImageIndex].description}
                  </p>
                )}
              </div>
            </Card>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {deviceImages.map((image, index) => (
                <button
                  key={`${image.device_id}-${index}`}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`relative rounded-lg overflow-hidden border-2 transition-colors ${
                    index === currentImageIndex ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <AspectRatio ratio={1}>
                    {image.url ? (
                      <img
                        src={image.url}
                        alt={`Thumbnail ${image.device_name || image.device_id}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <Camera className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </AspectRatio>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-3xl flex flex-col p-0 rounded-lg">
          <DialogHeader className="p-4 space-y-1">
            <DialogTitle className="text-lg">
              {deviceImages[currentImageIndex]?.device_name || "Gallery Image"}
            </DialogTitle>
          </DialogHeader>
          {deviceImages[currentImageIndex]?.url && (
            <div className="flex items-center justify-center p-4 pb-6 bg-black/5">
              <Zoom>
                <img 
                  src={deviceImages[currentImageIndex].url} 
                  alt={deviceImages[currentImageIndex].device_name || "Gallery image"} 
                  className="max-w-full max-h-[60vh] object-contain rounded-md cursor-zoom-in" 
                />
              </Zoom>
            </div>
          )}
          
          <div className="p-4 bg-muted/20">
            <div className="flex flex-wrap justify-between text-sm gap-4">
              <div>
                <p className="font-medium">
                  {deviceImages[currentImageIndex]?.device_name || deviceImages[currentImageIndex]?.device_id}
                </p>
                {deviceImages[currentImageIndex]?.description && (
                  <p className="text-muted-foreground mt-1">
                    {deviceImages[currentImageIndex].description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Gallery = () => {
  const [searchParams] = useSearchParams();
  const standalone = searchParams.get('standalone') === 'true';
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  if (standalone) {
    return <GalleryContent standalone={true} />;
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        {!isFullscreen && <AppSidebar />}
        <main className="flex-1">
          <GalleryContent standalone={false} />
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Gallery;
